/* eslint-disable @nx/enforce-module-boundaries */
import React, { FC, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { type ConsentsState, type OnConsentChangeParams, LegalConsents } from '@marriott/mi-legal-components';
import {
  updateConsentPayload,
  CONSENT_TYPE,
  ConsentErrorType,
  LEGAL_CONSENTS_JOIN_NOW_OPTION,
} from '../../modules/utils/legalConsentsHelper';
import { constants } from '../../modules/utils/constants';
import { useCreateAccountStore } from '../../modules/store/createAccountStore';
import { AccountLegalConsentsProps } from './AccountLegalConsents.types';
import { Messages, RichText } from '@marriott/mi-ui-library';

const getRequiredConsentSections = (consentsState: ConsentsState) => {
  return consentsState?.list?.filter(consentSection => consentSection?.sectionType === 'required') ?? [];
};

const getCanJoinNowByRequiredConsents = (consentsState: ConsentsState): boolean => {
  const requiredSections = getRequiredConsentSections(consentsState);

  if (requiredSections.length === 0) {
    return true;
  }

  return requiredSections.every(consentSection =>
    (consentSection?.checkboxes ?? []).every(checkbox => Boolean(checkbox?.checked))
  );
};

export const AccountLegalConsents: FC<AccountLegalConsentsProps> = ({
  sessionId,
  countryCode,
  locale,
  option = LEGAL_CONSENTS_JOIN_NOW_OPTION,
  isValidateErrors = false,
  isAuthorMode = false,
  customLoader,
  apiErrorMessage,
  isAccordion = false,
  openLinksInNewTab = false,
  customClassName,
  errorMessagePersonal,
  errorMessagePromotions,
}) => {
  const [consentState, setConsentState] = useState<ConsentsState>({
    list: [],
    sectionTypes: {},
    error: { type: ConsentErrorType.NONE, message: null },
    consentsLedger: {},
  });
  const [isLegalConsentsLoading, setIsLegalConsentsLoading] = useState(true);
  const [hasReceivedResponse, setHasReceivedResponse] = useState(false);
  const [hasApiError, setHasApiError] = useState(false);
  const { setConsents, setJoinNowEnabled, setConsentErrors } = useCreateAccountStore();
  const normalizedCountryCode = countryCode?.toUpperCase?.() ?? '';
  const localeToLowerCase = locale?.toLowerCase?.() ?? constants?.US_LOCALE;
  const normalizedLocale = localeToLowerCase === constants?.DEFAULT_LANG ? constants?.US_LOCALE : localeToLowerCase;
  const canJoinNowByRequiredConsents = useMemo(
    () => (hasReceivedResponse && !hasApiError ? getCanJoinNowByRequiredConsents(consentState) : false),
    [consentState, hasReceivedResponse, hasApiError]
  );
  const wrappedSetConsentState = useCallback(
    (newState: SetStateAction<ConsentsState>) => {
      setConsentState(newState);
      setHasReceivedResponse(true);
      if (typeof newState === 'object' && newState?.list?.length > 0) {
        const modifiedConsents = updateConsentPayload({ list: newState.list });
        setConsents(modifiedConsents);
      }
    },
    [setConsents, normalizedCountryCode]
  );

  useEffect(() => {
    if (consentState?.list?.length > 0) {
      const modifiedConsents = updateConsentPayload({ list: consentState.list });
      setConsents(modifiedConsents);
    }
  }, [consentState, normalizedCountryCode]);

  const handleApiError = () => {
    setHasApiError(true);
    return (
      <div className="py-4">
        <Messages messageType="error-sev1" className="legal-consent-error-msg pt-lg-0 pt-1">
          <RichText text={apiErrorMessage || ''} componentId="legal-consent-api-failure" />
        </Messages>
      </div>
    );
  };

  const handleConsentChange = useCallback(
    (params: OnConsentChangeParams) => {
      const { updatedConsentsState } = params;
      const normalizedConsentsState: ConsentsState = {
        list: updatedConsentsState.list,
        sectionTypes: updatedConsentsState.sectionTypes,
        consentsLedger: updatedConsentsState.consentsLedger,
        error: updatedConsentsState.error,
      };
      setConsentState(normalizedConsentsState);
      const modifiedConsents = updateConsentPayload({ list: normalizedConsentsState?.list });
      setConsents(modifiedConsents);
      if (normalizedConsentsState?.error?.type === ConsentErrorType.NONE) {
        setConsentErrors({});
      } else if (
        normalizedConsentsState?.error?.type === ConsentErrorType.MULTIPLE_VAILDATION_FAILED ||
        normalizedConsentsState?.error?.type === ConsentErrorType.ALL_ATOMIC_CONSENTS_NOT_SELECTED
      ) {
        let hasPrivacyMixed = false;
        let hasCommunicationMixed = false;
        for (const consent of modifiedConsents) {
          if (consent.isAllCheckedOrUnchecked === false) {
            if (consent.ctype === CONSENT_TYPE?.PRIVACY_CONSENTS) {
              hasPrivacyMixed = true;
            }
            if (consent.ctype === CONSENT_TYPE?.COMMUNICATION_PREFERENCES) {
              hasCommunicationMixed = true;
            }
          }
        }
        const legalConsentErrors: { [key: string]: string } = {
          ...(hasPrivacyMixed && errorMessagePromotions && { errorMessagePromotions: errorMessagePromotions }),
          ...(hasCommunicationMixed && errorMessagePersonal && { errorMessagePersonal: errorMessagePersonal }),
        };
        setConsentErrors(legalConsentErrors);
      }
    },
    [
      setConsentState,
      setConsents,
      setConsentErrors,
      normalizedCountryCode,
      errorMessagePersonal,
      errorMessagePromotions,
    ]
  );

  useEffect(() => {
    setJoinNowEnabled(!isLegalConsentsLoading && canJoinNowByRequiredConsents);
  }, [isLegalConsentsLoading, canJoinNowByRequiredConsents, setJoinNowEnabled]);

  return (
    <div className={clsx('account-legal-consents', customClassName)}>
      <LegalConsents
        sessionId={sessionId}
        countryCode={normalizedCountryCode}
        locale={normalizedLocale}
        option={option}
        isValidateErrors={isValidateErrors}
        consentsState={consentState}
        setConsentsState={wrappedSetConsentState}
        onConsentChange={handleConsentChange}
        isAuthorMode={isAuthorMode}
        customLoader={customLoader}
        onApiError={handleApiError}
        onLoading={setIsLegalConsentsLoading}
        linkBehavior={isAccordion ? 'accordion' : 'default'}
        anchorBehavior={openLinksInNewTab ? 'newTab' : 'default'}
      />
    </div>
  );
};
